import React, { useState, useEffect } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import axios from "axios";
import { decoratedAxiosPost } from "../../req_utils";
import { GENESIS_LOGO_COLOR } from "../../constants";
import { GENESIS_LOGO_NEW_BASE64 } from "../../genesis_logo_new_base64";
import { GENESIS_UTAH_LOGO_BASE64 } from "../../genesis_utah_logo_base64";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { decoratedFetch } from "../../req_utils";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.fonts = {
  Roboto: {
    normal: "Roboto-Regular.ttf",
    bold: "Roboto-Medium.ttf",
    italics: "Roboto-Italic.ttf",
    bolditalics: "Roboto-MediumItalic.ttf",
  },
};

const SECONDARY_COLOR = "#005691";
const COVER_PAGE_X_MARGIN = 40;

const FieldWorkOrderDialog = ({
  open,
  handleClose: handleCloseProp,
  proposal,
  project,
  exclusionCheckboxes = {},
  installDate = null,
  startTime = "",
  includedArea = "",
  fwoInclusions = [],
  scopeOfWorkNotes = "",
  footerNotes = "",
}) => {
  const [workOrderName, setWorkOrderName] = useState(
    `${project?.name ? project.name + " " : ""}Field Work Order`
  );
  const [workOrderNameError, setWorkOrderNameError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    setWorkOrderName(
      `${project?.name ? project.name + " " : ""}Field Work Order`
    );
  }, [open, project]);

  const handleClose = (e, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    handleCloseProp();
  };

  const getPDFBlob = (docDefinition) => {
    return new Promise((resolve, reject) => {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.getBuffer((buffer) => {
        const blob = new Blob([buffer], { type: "application/pdf" });
        resolve(blob);
      });
    });
  };

  const convertImageToBase64 = async (imagePath) => {
    try {
      const response = await fetch(imagePath);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          resolve(`data:image/png;base64,${base64String}`);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!workOrderName) {
      setWorkOrderNameError(true);
      return;
    }

    try {
      setLoading(true);

      const branch_address = project?.branch;
      const is_ca_branch = branch_address?.state === "CA";
      
      let branch_address_line_1 = "";
      let branch_address_line_2 = "";
      let branch_address_phone_no = "";
      let branch_address_contractor_no = "";
      let branch_address_dir_no = "";
      
      if (branch_address) {
        branch_address_line_1 = `${branch_address.line_1}${branch_address.line_2 ? ` ${branch_address.line_2}` : ""}\n`;
        branch_address_line_2 = `${branch_address.city}, ${branch_address.state} ${branch_address.zip}\n`;
        branch_address_phone_no = `Tel: ${branch_address.phone_no}\n`;
        branch_address_contractor_no = `Contractor Lic. #${branch_address.contractor_no}\n`;
        branch_address_dir_no = `DIR #${branch_address.dir_no}\n`;
      }

      // Get contact (customer) info
      const customer = proposal?.customer || {};
      let contact_name = "";
      let contact_phone_no = "";
      if (customer && typeof customer === 'object' && !Array.isArray(customer)) {
        if (customer.first_name || customer.last_name) {
          contact_name = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
        }
        contact_phone_no = customer.cell_no || "";
      }

      // Get location info (split into two lines like proposal PDF)
      const location = proposal?.location || {};
      let location_line_1 = "";
      let location_line_2 = "";
      if (location && typeof location === 'object' && !Array.isArray(location) && location.address) {
        location_line_1 = `${location.address?.line_1 || ""}${location.address?.line_2 ? ` ${location.address.line_2}` : ""}`;
        location_line_2 = `${location.address?.city || ""}${location.address?.state ? `, ${location.address.state}` : ""}${location.address?.zip ? ` ${location.address.zip}` : ""}`;
      }

      // Get project info
      const project_name = project?.name || "";
      const job_number = project?.custom_id || "";
      const account_name = project?.account?.name || "";

      // Get install date and start time (installDate is now a text string)
      const installDateStr = installDate || "";
      const startTimeStr = startTime || "";

      // Get labor inclusions (format for itemized table)
      const laborInclusions = (fwoInclusions || []).filter(
        (incl) => (incl.category || "Labor") === "Labor",
      );

      // Generate itemized table for inclusions (Description only)
      const generateItemizedInclusionsTable = (rows) => {
        const tableBody = [
          ...rows.map((row, index) => [
            { 
              text: row.description || "", 
              fontSize: 8,
              border: [false, false, false, index < rows.length - 1], // Only bottom border except for last row
            },
          ]),
        ];

        return {
          table: {
            headerRows: 0,
            widths: ["100%"],
            body: tableBody,
          },
          layout: {
            hLineWidth: function(i, node) {
              return 0;
            },
            vLineWidth: function(i, node) {
              return 0;
            },
            paddingBottom: function(i, node) {
              return i < node.table.body.length - 1 ? 1 : 0; // Only add padding for rows that have borders
            },
          },
          fillColor: "white",
        };
      };

      // Get exclusions with checked status from checkbox state
      const FIELD_WORK_ORDER_EXCLUSIONS = [
        "DEMOLITION",
        "FURN / EQUIP MOVING",
        "FLOOR PATCH / REPAIR",
        "MOISTURE TESTING",
        "MOISTURE TREATMENT",
        "BORDERS, INLAYS, OR PATTERNS",
        "NON-STANDARD WORK HOURS",
        "OTHER",
      ];
      
      const exclusions = FIELD_WORK_ORDER_EXCLUSIONS.map((exclusion) => {
        const isChecked = exclusionCheckboxes[exclusion] === true;
        return {
          description: exclusion,
          checked: isChecked,
        };
      });

      // Get scope of work notes and footer notes from proposal
      const scopeOfWorkNotesText = proposal?.scope_of_work_notes || scopeOfWorkNotes || "";
      const footerNotesText = proposal?.footer_notes || footerNotes || "";

      // Load job inspection report image
      const jobInspectionReportImage = await convertImageToBase64('/job_inspection_report.png');

      const docDefinition = {
        content: [
          // Header with smaller logo and branch address
          {
            columns: [
              {
                image: is_ca_branch
                  ? GENESIS_LOGO_NEW_BASE64
                  : GENESIS_UTAH_LOGO_BASE64,
                width: is_ca_branch ? 100 : 125,
                height: is_ca_branch ? 40 : 50,
                alignment: "left",
              },
              {
                stack: [
                  {
                    text: is_ca_branch
                      ? "Genesis, Inc."
                      : "Genesis Floor Covering Utah, LLC",
                    fontSize: 9,
                    bold: true,
                  },
                  {
                    text: branch_address_line_1.replace(/\n/g, ""),
                    fontSize: 8,
                    margin: [0, 1, 0, 0],
                  },
                  {
                    text: branch_address_line_2.replace(/\n/g, ""),
                    fontSize: 8,
                    margin: [0, 1, 0, 0],
                  },
                  {
                    text: branch_address_phone_no.replace(/\n/g, ""),
                    fontSize: 8,
                    margin: [0, 1, 0, 0],
                  },
                ],
                width: "*",
                alignment: "right",
              },
            ],
            margin: [COVER_PAGE_X_MARGIN, 8, COVER_PAGE_X_MARGIN, 3],
          },
          // Title
          {
            text: "Field Work Order",
            fontSize: 24,
            bold: true,
            color: "#003086",
            alignment: "center",
            margin: [COVER_PAGE_X_MARGIN, 3, COVER_PAGE_X_MARGIN, 5],
          },
          // Job Number - Big and prominent
          {
            text: `Job #: ${job_number || ""}`,
            fontSize: 18,
            bold: true,
            color: "#003086",
            margin: [COVER_PAGE_X_MARGIN, 0, COVER_PAGE_X_MARGIN, 5],
          },
          // Information in two columns: Left = Project + Location + Account, Right = Contact + Install Date + Start Time
          {
            margin: [0, 0, 0, 2],
            table: {
              widths: ["50%", "50%"],
              body: [
                [
                  // Left column: Project, Location, Account
                  {
                    stack: [
                      {
                        text: "Project:",
                        bold: true,
                        fontSize: 9,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: project_name || "",
                        fontSize: 9,
                        margin: [0, 0, 0, 3],
                      },
                      {
                        text: "Location:",
                        bold: true,
                        fontSize: 9,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: location_line_1 || "",
                        fontSize: 9,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: location_line_2 || "",
                        fontSize: 9,
                        margin: [0, 0, 0, 3],
                      },
                      {
                        text: "Account:",
                        bold: true,
                        fontSize: 9,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: account_name || "",
                        fontSize: 9,
                        margin: [0, 0, 0, 0],
                      },
                    ],
                  },
                  // Right column: Install Date, Start Time, Contact, Telephone
                  {
                    stack: [
                      {
                        text: "Install Date:",
                        bold: true,
                        fontSize: 12,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: installDateStr || "",
                        fontSize: 12,
                        margin: [0, 0, 0, 3],
                      },
                      {
                        text: "Start Time:",
                        bold: true,
                        fontSize: 12,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: startTimeStr || "",
                        fontSize: 12,
                        margin: [0, 0, 0, 3],
                      },
                      {
                        text: "Contact:",
                        bold: true,
                        fontSize: 9,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: contact_name || "",
                        fontSize: 9,
                        margin: [0, 0, 0, 1],
                      },
                      {
                        text: `Telephone: ${contact_phone_no || ""}`,
                        fontSize: 9,
                        margin: [0, 0, 0, 0],
                      },
                    ],
                  },
                ],
              ],
            },
            layout: "noBorders",
          },
          // Title section box (SCOPE OF WORK header, Included Area, Scope of Work Notes) - grey background
          {
            table: {
              widths: ["*"],
              body: [[
                {
                  stack: [
                    // SCOPE OF WORK header (centered, inside box)
                    {
                      text: "SCOPE OF WORK:",
                      bold: true,
                      fontSize: 10,
                      alignment: "center",
                      margin: [0, 0, 0, 2],
                    },
                    // Included Area (centered)
                    ...(includedArea ? [{
                      text: includedArea,
                      fontSize: 9,
                      alignment: "center",
                      margin: [0, 0, 0, 2],
                    }] : []),
                    // Scope of Work Notes in blue text (centered)
                    ...(scopeOfWorkNotesText ? [
                      {
                        text: scopeOfWorkNotesText,
                        fontSize: 9,
                        color: "#2E67D1", // Blue color
                        alignment: "center",
                        margin: [0, 1, 0, 1],
                      },
                    ] : []),
                  ],
                  margin: [8, 4, 8, 4],
                }
              ]],
            },
            layout: {
              hLineWidth: function(i, node) {
                return 1;
              },
              vLineWidth: function(i, node) {
                return 1;
              },
              hLineColor: function(i, node) {
                return '#000000';
              },
              vLineColor: function(i, node) {
                return '#000000';
              },
              paddingLeft: function(i, node) {
                return 0;
              },
              paddingRight: function(i, node) {
                return 0;
              },
              paddingTop: function(i, node) {
                return 0;
              },
              paddingBottom: function(i, node) {
                return 0;
              },
            },
            fillColor: "#c0c0c0", // Light grey background
            margin: [0, 0, 0, 0],
          },
          // Scope of Work section in a bordered box (Labor inclusions) - connected to title box
          {
            table: {
              widths: ["*"],
              body: [[
                {
                  stack: [
                    // Divider line at top (if there are inclusions)
                    ...(laborInclusions.length > 0 ? [
                      {
                        table: {
                          widths: ["*"],
                          body: [[
                            {
                              text: "",
                              border: [false, true, false, false],
                            }
                          ]],
                        },
                        layout: {
                          hLineWidth: function(i, node) {
                            return 0;
                          },
                          vLineWidth: function(i, node) {
                            return 0;
                          },
                          paddingTop: function(i, node) {
                            return 1;
                          },
                        },
                        margin: [0, 0, 0, 1],
                      },
                    ] : []),
                    // Labor inclusions in itemized table format
                    ...(laborInclusions.length > 0 
                      ? [generateItemizedInclusionsTable(laborInclusions)]
                      : []),
                  ],
                  margin: [8, 6, 8, 6],
                }
              ]],
            },
            layout: {
              hLineWidth: function(i, node) {
                // No top border to connect with the box above
                if (i === 0) return 0;
                return 1;
              },
              vLineWidth: function(i, node) {
                return 1;
              },
              hLineColor: function(i, node) {
                return '#000000';
              },
              vLineColor: function(i, node) {
                return '#000000';
              },
              paddingLeft: function(i, node) {
                return 0;
              },
              paddingRight: function(i, node) {
                return 0;
              },
              paddingTop: function(i, node) {
                return 0;
              },
              paddingBottom: function(i, node) {
                return 0;
              },
            },
            margin: [0, 0, 0, 5],
          },
          // Exclusions section - title and checkbox grid (4 columns, 2 rows)
          {
            text: "EXCLUDED FROM OUR WORK:",
            bold: true,
            italics: true,
            fontSize: 10,
            margin: [0, 5, 0, 3],
          },
          {
            table: {
              headerRows: 0,
              widths: ["25%", "25%", "25%", "25%"],
              body: (() => {
                const rows = [];
                for (let i = 0; i < exclusions.length; i += 4) {
                  const row = [];
                  for (let j = 0; j < 4; j++) {
                    const exclusion = exclusions[i + j];
                    if (exclusion) {
                      row.push({
                        stack: [
                          {
                            columns: [
                              {
                                // Checkbox square
                                canvas: [
                                  {
                                    type: "rect",
                                    x: 0,
                                    y: 0,
                                    w: 10,
                                    h: 10,
                                    lineWidth: 1,
                                    lineColor: "#000000",
                                  },
                                  // X mark if checked
                                  ...(exclusion.checked ? [
                                    {
                                      type: "line",
                                      x1: 2,
                                      y1: 2,
                                      x2: 8,
                                      y2: 8,
                                      lineWidth: 1.5,
                                      lineColor: "#000000",
                                    },
                                    {
                                      type: "line",
                                      x1: 8,
                                      y1: 2,
                                      x2: 2,
                                      y2: 8,
                                      lineWidth: 1.5,
                                      lineColor: "#000000",
                                    },
                                  ] : []),
                                ],
                                width: 12,
                                margin: [0, 0, 5, 0],
                              },
                              {
                                text: exclusion.description || "",
                                fontSize: 8,
                                width: "*",
                              },
                            ],
                            margin: [0, 1, 0, 1],
                          },
                        ],
                      });
                    } else {
                      row.push({ text: "", margin: [0, 0, 0, 0] });
                    }
                  }
                  rows.push(row);
                }
                return rows;
              })(),
            },
            layout: {
              hLineWidth: function(i, node) {
                return 0;
              },
              vLineWidth: function(i, node) {
                return 0;
              },
            },
            margin: [0, 0, 0, 5],
          },
          // Job Inspection Report Image
          ...(jobInspectionReportImage ? [
            {
              image: jobInspectionReportImage,
              width: 515,
              alignment: "center",
              margin: [0, 5, 0, 5],
            },
          ] : []),
          // Footer Notes
          ...(footerNotesText ? [
            {
              text: footerNotesText,
              fontSize: 12,
              color: "#FF0000",
              alignment: "center",
              margin: [0, 5, 0, 2],
            },
          ] : []),
        ],
        styles: {
          headerRight: {
            alignment: "right",
            fontSize: 9,
            color: SECONDARY_COLOR,
          },
          headerCenter: {
            bold: true,
            fontSize: 18,
            alignment: "center",
            margin: [0, 18, 0, 20],
          },
        },
        defaultStyle: {
          columnGap: 20,
        },
      };

      const pdfBlob = await getPDFBlob(docDefinition);
      const fileName = workOrderName + ".pdf";

      const { data } = await decoratedAxiosPost(
        "/get_s3_presigned_urls_for_upload",
        {
          project_id: project.id,
          files: [
            {
              name: fileName,
              type: "application/pdf",
            },
          ],
        }
      );

      const newFile = {
        name: fileName,
        type: "pdf",
        s3_key: data[0].s3_key,
      };

      await axios.put(data[0].presigned_url, pdfBlob, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      // Create project file metadata
      await decoratedAxiosPost("/create_project_files_metadata", {
        project_id: project.id,
        files: [
          {
            name: fileName,
            type: "pdf",
            s3_key: newFile.s3_key,
          },
        ],
      });

      // Get presigned URL for preview
      try {
        const { data: downloadData } = await decoratedAxiosPost(
          "/get_s3_presigned_url_for_download",
          {
            s3_key: newFile.s3_key,
          }
        );
        setLoading(false);
        handleCloseProp(true, newFile, downloadData.presigned_url);
      } catch (error) {
        console.error("Error previewing file:", error);
        setLoading(false);
        handleCloseProp(true, newFile, null);
      }
    } catch (error) {
      setLoading(false);
      if (error.response?.data?.msg) {
        setAlertMessage(error.response.data.msg);
      } else {
        setAlertMessage("An error occurred when creating field work order");
      }
      console.error("Error creating field work order:", error);
    }
  };

  const onWorkOrderNameChange = (newName) => {
    setWorkOrderNameError(false);
    setWorkOrderName(newName);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      disableEscapeKeyDown
      disableScrollLock
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Create Field Work Order PDF
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: "5px" }}>
          {alertMessage && (
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Alert
                onClose={() => setAlertMessage("")}
                severity={"error"}
                sx={{
                  width: "80%",
                  borderRadius: 8,
                  marginBottom: "15px",
                }}
              >
                {alertMessage}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="File Name"
              variant="outlined"
              fullWidth
              autoFocus
              value={workOrderName}
              onChange={(e) => onWorkOrderNameChange(e.target.value)}
              error={workOrderNameError}
              helperText={workOrderNameError ? "Required" : ""}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ pt: "15px" }}>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={handleSubmit}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FieldWorkOrderDialog;

